import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { notify } from 'react-notify-toast';
import { useHistory } from 'react-router-dom';

import AddComission from './AddComission';
import ConfirmDelete from './ConfirmDelete';
import Selector from './Selector';
import List from './List';
import CopyMails from './CopyMails';
import TabbedInterface from './TabbedInterface ';
import clientAxios from '../../clients/clientAxios';

export default function Commission() {
  const history = useHistory();

  const [showAddCommission, setShowAddCommission] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showCopyMails, setShowCopyMails] = useState(false);
  const [activeDownFilter, setActiveDownFilter] = useState('active');
  const [isLoading, setIsLoading] = useState({
    component: true,
    singleCommission: false,
  });
  const [mentorList, setMentorList] = useState([]);
  const [selectedCommission, setSelectedCommission] = useState('');
  const [commission, setCommission] = useState({});
  const [crList, setCrList] = useState([]);
  const [crListLoading, setCrListLoading] = useState(true);
  const fetchedMentorIds = useRef(new Set());

  const headerConfig = {
    headers: { 'auth-token': localStorage.getItem('JWT') || ' ' },
  };

  let counter = 0;

  useEffect(() => {
    if (counter === 0) {
      ++counter;
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    bringData();
    //eslint-disable-next-line
  }, [activeDownFilter]);

  useEffect(() => {
    setCrListLoading(false);
  }, [crList]);

  useEffect(() => {
    const objectRefs = [commission.mentor, commission.helper].filter(
      (ref) => ref && typeof ref === 'object' && (ref._id || ref.id)
    );
    if (objectRefs.length > 0) {
      setMentorList((previousList) => {
        const knownIds = new Set(
          previousList.map((mentor) => mentor._id || mentor.id)
        );
        const freshMentors = objectRefs.filter(
          (ref) => !knownIds.has(ref._id || ref.id)
        );
        return freshMentors.length > 0
          ? [...previousList, ...freshMentors]
          : previousList;
      });
    }
    const mentorRefs = [commission.mentor, commission.helper].filter(
      (ref) => typeof ref === 'string' && ref.trim() !== ''
    );
    const missingIds = mentorRefs.filter((ref) => {
      const alreadyKnown = mentorList.some(
        (mentor) => (mentor._id || mentor.id) === ref
      );
      return !alreadyKnown && !fetchedMentorIds.current.has(ref);
    });
    missingIds.forEach((missingId) => {
      fetchedMentorIds.current.add(missingId);
      clientAxios
        .get(`/mentors/${missingId}`, headerConfig)
        .then(({ data }) => {
          const payload = data?.data ?? data;
          const mentorObject = Array.isArray(payload)
            ? payload.find((item) => (item._id || item.id) === missingId) || payload[0]
            : payload;
          if (mentorObject && (mentorObject._id || mentorObject.id)) {
            setMentorList((previousList) => {
              const alreadyThere = previousList.some(
                (mentor) =>
                  (mentor._id || mentor.id) ===
                  (mentorObject._id || mentorObject.id)
              );
              return alreadyThere
                ? previousList
                : [...previousList, mentorObject];
            });
          }
        })
        .catch(() => {
          fetchedMentorIds.current.delete(missingId);
        });
    });
    //eslint-disable-next-line
  }, [commission.mentor, commission.helper, mentorList]);

  const getAllClassrooms = () => {
    let classroom = clientAxios.get(
      '/classroom/search/',
      {
        params: { isActive: activeDownFilter, sortBy: '-createdAt' },
      },
    );

    axios
      .all([classroom])
      .then(
        axios.spread((...response) => {
          if (response.length > 0 && response[0]?.data) {
            setIsLoading({ ...isLoading, component: false });
            setCrList(
              response[0].data.filter((i) => i.classroom !== undefined)
            );
          } else {
            notify?.show(response.message, 'error');
            history.push('/');
          }
        })
      )
      .catch((err) => {
        notify.show(err?.response?.data?.message, 'error');
        history.push('/');
      });
  };

  const bringData = () => {
    let mentors = clientAxios.get('/mentors', {
      params: { limit: 500 },
      ...headerConfig,
    });

    axios
      .all([mentors])
      .then(
        axios.spread((...response) => {
          if (response.length > 0 && response[0]?.data) {
            setMentorList(response[0].data?.data);
            setIsLoading({ ...isLoading, component: false });
          } else {
            notify?.show(response.message, 'error');
            history.push('/');
          }
        })
      )
      .catch((err) => {
        notify.show(err?.response?.data?.message, 'error');
        history.push('/');
      });
  };

  const searchMentors = async (name) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      return [];
    }
    const { data } = await clientAxios.get('/mentors', {
      params: { name: trimmedName, limit: 10 },
      ...headerConfig,
    });
    const results = data?.data ?? data ?? [];
    const normalizedResults = Array.isArray(results) ? results : [];
    setMentorList((previousList) => {
      const knownIds = new Set(
        previousList.map((mentor) => mentor._id || mentor.id)
      );
      const freshMentors = normalizedResults.filter(
        (mentor) => mentor && !knownIds.has(mentor._id || mentor.id)
      );
      return freshMentors.length > 0
        ? [...previousList, ...freshMentors]
        : previousList;
    });
    return normalizedResults;
  };

  const handleCommissionChange = (obj) => {
    if (!obj) {
      getStudentsInClassroom(selectedCommission);
    } else {
      let { value } = obj.target;
      setSelectedCommission(value);
      setIsLoading({ ...isLoading, singleCommission: true });

      getStudentsInClassroom(value);
    }
  };

  const getStudentsInClassroom = (commissionId) => {
    clientAxios
      .get(`/classroom/getStudentsInClassroom/${commissionId}`, headerConfig)
      .then(({ data }) => {
        setIsLoading({ ...isLoading, singleCommission: false });
        const classroomData = data.data[0];
        const normalizeMentorRef = (ref) => {
          if (ref && typeof ref === 'object') {
            return ref._id || ref.id || ref;
          }
          return ref;
        };
        const mentorRefs = [classroomData.mentor, classroomData.helper].filter(
          (ref) => ref && typeof ref === 'object' && (ref._id || ref.id)
        );
        if (mentorRefs.length > 0) {
          setMentorList((previousList) => {
            const knownIds = new Set(
              previousList.map((mentor) => mentor._id || mentor.id)
            );
            const freshMentors = mentorRefs.filter(
              (mentor) => !knownIds.has(mentor._id || mentor.id)
            );
            return freshMentors.length > 0
              ? [...previousList, ...freshMentors]
              : previousList;
          });
        }
        setCommission({
          ...classroomData,
          mentor: normalizeMentorRef(classroomData.mentor),
          helper: normalizeMentorRef(classroomData.helper),
          timeFrom: classroomData.timeFrom,
          timeTo: classroomData.timeTo,
          course: classroomData.course,
          schedule: classroomData.schedule,
          isActive: classroomData.isActive ? classroomData.isActive : '',
          students: classroomData.students,
          courseSummary: classroomData.courseSummary,
          slackChannel: classroomData.slackChannel
            ? classroomData.slackChannel
            : '',
          published: classroomData?.published,
          initialDate: classroomData?.initialDate || '',
        });
      })
      .catch((err) => {
        notify.show(err.response.data.message, 'error');
        history.push('/');
      });
  };
  return (
    <React.Fragment>
      <Container fluid="lg" className="py-4">
        <CopyMails
          show={showCopyMails}
          onHide={() => setShowCopyMails(!showCopyMails)}
          commission={commission.classroom}
          students={commission.students}
        />
        <AddComission
          show={showAddCommission}
          onHide={() => setShowAddCommission(!showAddCommission)}
          updateCommission={bringData}
        />
        <ConfirmDelete
          show={showConfirmDelete}
          onHide={() => setShowConfirmDelete(!showConfirmDelete)}
          idToDelete={commission._id}
          updateCommission={bringData}
        />
        <Selector
          commissionSelected={(obj) => handleCommissionChange(obj)}
          commission={(commission && Object.keys(commission).length > 0) ? commission : { students: false }}
          confirmDelete={() => setShowConfirmDelete(!showConfirmDelete)}
          addComission={() => setShowAddCommission(!showAddCommission)}
          isLoading={isLoading}
          crList={crList}
          copyMails={() => setShowCopyMails(!showCopyMails)}
          activeDownFilter={activeDownFilter}
          setActiveDownFilter={(value) => setActiveDownFilter(value)}
          crListLoading={crListLoading}
          setCrListLoading={(value) => setCrListLoading(value)}
          setCommission={(value) => setCommission(value)}
          getAllClassrooms={getAllClassrooms}
        />
        <Row className="mt-2">
          <List commission={commission} />
          <TabbedInterface
            mentorList={mentorList}
            commission={commission}
            setCommission={setCommission}
            searchMentors={searchMentors}
          />
        </Row>
      </Container>
    </React.Fragment>
  );
}
